import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, ButtonBase, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ConfigCard from './ConfigCard';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChipsBox, SubLabel, focusRing } from './_PdiHelpers';

// The 24 legacy field keys the storefront's detail page understands (matches
// detail.component.ts `allowedFields`). Used as the available chip palette
// for all four slots in this card. `category_N` labels are overridden by
// the shop-specific labels from `kasvars` when present.
export const ALL_FIELDS = [
    { key: 'id',          i18nKey: 'cfg_pdi_details_id',          fallback: 'ID' },
    { key: 'brand',       i18nKey: 'cfg_pdi_details_brand',       fallback: 'Supplier' },
    { key: 'name',        i18nKey: 'cfg_pdi_details_name',        fallback: 'Name' },
    { key: 'reference',   i18nKey: 'cfg_pdi_details_reference',   fallback: 'Reference' },
    { key: 'color',       i18nKey: 'cfg_pdi_details_color',       fallback: 'Color' },
    { key: 'color_brand', i18nKey: 'cfg_pdi_details_color_brand', fallback: 'Variant' },
    { key: 'season',      i18nKey: 'cfg_pdi_details_season',      fallback: 'Season' },
    ...[1,2,3,4,5,6,7].map(n => ({
        key:      `category_${n}`,
        i18nKey:  `cfg_pdi_details_category_${n}`,
        fallback: `Category ${n}`,
    })),
    ...[1,2,3,4,5].map(n => ({
        key:      `description_${n}`,
        i18nKey:  `cfg_pdi_details_description_${n}`,
        fallback: `Description ${n}`,
    })),
    ...[1,2,3,4,5].map(n => ({
        key:      `detail_${n}`,
        i18nKey:  `cfg_pdi_details_detail_${n}`,
        fallback: `Details ${n}`,
    })),
];

// Five slots, each backed by one settings key:
//   - browserTab     → SHOW_FIELDS_ON_DETAIL_TAB       (free-form template)
//   - pageHeading    → SHOW_FIELDS_ON_DETAIL_TITLE     (free-form template)
//   - pageSubheading → SHOW_FIELDS_ON_DETAIL_SUBHEADING (free-form template)
//   - urlSlug        → DETAIL_PAGE_URL_SECTION         (CSV — no literal text)
//   - schemaName     → SEO_PRODUCT_NAME                (free-form template; the
//     product name search engines get in the schema. Empty = the reference,
//     unchanged from before the setting existed)
// The templates support literal-text chips (for parens, separators,
// etc.); the URL slug doesn't (storefront splits on `,` and only accepts
// known field keys).

let _chipSeq = 0;
const _newId = () => `chip_${++_chipSeq}`;
export const tokenChip = (key)  => ({ id: _newId(), key });
export const textChip  = (text) => ({ id: _newId(), text });

export const isTextChip  = (c) => c && c.text !== undefined;
export const isTokenChip = (c) => c && c.key  !== undefined;

// F13: O(1) lookup instead of a linear ALL_FIELDS.find per rendered chip.
export const FIELD_BY_KEY = new Map(ALL_FIELDS.map(f => [f.key, f]));

// The settings key behind each slot. Each slot also carries `saved`: the raw
// string as it is stored on the shop (null = the key doesn't exist there), so
// SoftTouch users can check what the storefront really reads.
export const SLOT_SETTINGS = {
    browserTab:     'SHOW_FIELDS_ON_DETAIL_TAB',
    pageHeading:    'SHOW_FIELDS_ON_DETAIL_TITLE',
    pageSubheading: 'SHOW_FIELDS_ON_DETAIL_SUBHEADING',
    urlSlug:        'DETAIL_PAGE_URL_SECTION',
    schemaName:     'SEO_PRODUCT_NAME',
};

const RAW_TOGGLE_STORAGE = 'webshopAdmin.pdiTitleRaw';

// A setting as loaded: its stored string, or null when the shop lacks the key.
export const rawSetting = (v) => (v === undefined || v === null ? null : String(v));

// One slot's value as it gets saved (CSV for the URL slug, template otherwise).
export const serializeSlot = (slot, used) =>
    slot === 'urlSlug' ? serializeChipsToCsv(used) : serializeChipsToTemplate(used);

// After a successful save: the saved titleAndUrl, with `saved` moved to the
// written string for every slot that changed. Unchanged slots keep their raw
// value (the save didn't touch them).
export const withSavedTitleUrl = (current = {}, original = {}) =>
    Object.fromEntries(Object.keys(SLOT_SETTINGS).map((slot) => {
        const now    = serializeSlot(slot, current[slot]?.used);
        const before = serializeSlot(slot, original[slot]?.used);
        const saved  = now !== before ? now : original[slot]?.saved;
        return [slot, { ...(current[slot] || { used: [] }), saved }];
    }));

// SoftTouch-only line under a slot: the stored string, quoted so leading and
// trailing spaces show, plus what a save would write when that differs.
const RawSavedValue = ({ setting, saved, pending, tr }) => {
    const quote = (s) => `"${s}"`;
    const savedText = saved === null || saved === undefined
        ? tr('sft_raw_missing', 'does not exist on this shop')
        : quote(saved);
    return (
        <Box sx={{
            mt: 0.75, px: 1, py: 0.5, borderRadius: 1,
            backgroundColor: 'rgba(0,0,0,0.04)',
            fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.5,
            color: 'text.secondary', wordBreak: 'break-all',
        }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{setting}</Box>
            {' = '}
            <Box component="span" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>{savedText}</Box>
            {pending !== null && (
                <Box sx={{ color: 'warning.main', whiteSpace: 'pre-wrap' }}>
                    {tr('sft_raw_after_save', 'after saving')}: {quote(pending)}
                </Box>
            )}
        </Box>
    );
};
RawSavedValue.propTypes = { setting: PropTypes.string, saved: PropTypes.string, pending: PropTypes.string, tr: PropTypes.func };

// A chip in a slot that can be dragged to another position (mouse, touch,
// or keyboard: focus the chip, space, arrows, space). The × and the free-text
// input keep working as before: pressing or typing in them never starts a drag.
const SortableSlotChip = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const ownControl = (e) => e.target.closest('button, input');
    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            onPointerDown={(e) => { if (!ownControl(e)) listeners?.onPointerDown?.(e); }}
            onKeyDown={(e) => { if (!ownControl(e)) listeners?.onKeyDown?.(e); }}
            sx={{
                display: 'inline-flex',
                transform: CSS.Transform.toString(transform), transition,
                opacity: isDragging ? 0.5 : 1, cursor: 'grab', touchAction: 'none',
                borderRadius: 999, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
        >
            {children}
        </Box>
    );
};
SortableSlotChip.propTypes = { id: PropTypes.string, children: PropTypes.node };

export const SlotEditor = ({
    titleLabel, helpText, slot, used, available, allowText,
    onSet, labelFor, tr, raw,
}) => {
    const setChip = (id, patch) => onSet(used.map(c => c.id === id ? { ...c, ...patch } : c));
    const remove  = (id)        => onSet(used.filter(c => c.id !== id));
    const append  = (chip)      => onSet([...used, chip]);
    // a few pixels of movement before a drag starts, so a click stays a click
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const ids = used.map(c => c.id);
        onSet(arrayMove(used, ids.indexOf(active.id), ids.indexOf(over.id)));
    };

    return (
        <Box>
            <SubLabel>{titleLabel}</SubLabel>
            {helpText && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75, lineHeight: 1.4 }}>
                    {helpText}
                </Typography>
            )}
            <ChipsBox>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={used.map(c => c.id)} strategy={rectSortingStrategy}>
                        {used.map(c => (
                            <SortableSlotChip key={c.id} id={c.id}>
                                {isTextChip(c) ? (
                                    <TextChipBubble
                                        value={c.text}
                                        onChange={(v) => setChip(c.id, { text: v })}
                                        onRemove={() => remove(c.id)}
                                        tr={tr}
                                    />
                                ) : (
                                    <TokenChipBubble
                                        label={labelFor(c.key)}
                                        onRemove={() => remove(c.id)}
                                    />
                                )}
                            </SortableSlotChip>
                        ))}
                    </SortableContext>
                </DndContext>
                {used.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1, py: 0.5 }}>
                        {tr('cfg_pdi_title_slot_empty', 'Empty — pick a field below or add free text.')}
                    </Typography>
                )}
                {allowText && (
                    <ButtonBase
                        onClick={() => append(textChip(' '))}
                        title={tr('cfg_pdi_title_add_text', 'Add free text')}
                        sx={{
                            flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                            px: 1, py: 0.4,
                            borderRadius: 999,
                            border: '1px dashed', borderColor: 'divider',
                            color: 'text.secondary',
                            fontSize: '0.7rem', fontWeight: 600,
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                            ...focusRing,
                        }}
                    >
                        <TextFieldsIcon sx={{ fontSize: 13 }} />
                        {tr('cfg_pdi_title_add_text_short', '+ Text')}
                    </ButtonBase>
                )}
            </ChipsBox>
            {raw && <RawSavedValue {...raw} tr={tr} />}
            {available.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {available.map(field => (
                        <ButtonBase
                            key={`${slot}-${field.key}`}
                            onClick={() => append(tokenChip(field.key))}
                            sx={{
                                px: 1, py: 0.4, borderRadius: 999,
                                border: '1px dashed', borderColor: 'divider',
                                color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600,
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                                ...focusRing,
                            }}
                        >
                            + {labelFor(field.key)}
                        </ButtonBase>
                    ))}
                </Box>
            )}
        </Box>
    );
};
SlotEditor.propTypes = {
    titleLabel: PropTypes.node, helpText: PropTypes.node, slot: PropTypes.string,
    used: PropTypes.array, available: PropTypes.array, allowText: PropTypes.bool,
    onSet: PropTypes.func, labelFor: PropTypes.func, tr: PropTypes.func,
    raw: PropTypes.object,
};

const TokenChipBubble = React.memo(({ label, onRemove }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25,
        px: 1, py: 0.25, borderRadius: 999,
        backgroundColor: 'rgba(25,118,210,0.10)',
        color: 'primary.main',
        border: '1px solid', borderColor: 'rgba(25,118,210,0.30)',
        fontSize: '0.75rem', fontWeight: 600,
    }}>
        {label}
        <IconButton size="small" onClick={onRemove} sx={{ p: 0, ml: 0.25, color: 'inherit' }}>
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
    </Box>
));
TokenChipBubble.propTypes = { label: PropTypes.node, onRemove: PropTypes.func };

const TextChipBubble = ({ value, onChange, onRemove, tr }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25,
        backgroundColor: 'rgba(0,0,0,0.04)',
        border: '1px solid', borderColor: 'divider',
        borderRadius: 999,
        pl: 0.25, pr: 0.5, py: 0.25,
    }}>
        <DragIndicatorIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
        <TextField
            size="small"
            variant="standard"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={tr('cfg_pdi_title_text_placeholder', 'free text')}
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', px: 0.25 } }}
            sx={{ minWidth: 24, '& input': { padding: 0, width: `${Math.max(24, (value || '').length * 7 + 8)}px` } }}
        />
        <IconButton size="small" onClick={onRemove} sx={{ p: 0, color: 'text.disabled' }}>
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
    </Box>
);
TextChipBubble.propTypes = { value: PropTypes.string, onChange: PropTypes.func, onRemove: PropTypes.func, tr: PropTypes.func };

const ProductTitleAndUrlCard = ({ value = {}, onChange, shopFieldLabels, savedValue, showRaw = false, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    // SoftTouch users: toggle that shows the raw stored string per slot.
    // Remembered per browser; storage may be unavailable, so guarded.
    const [rawOpen, setRawOpen] = useState(() => {
        try { return localStorage.getItem(RAW_TOGGLE_STORAGE) === '1'; } catch { return false; }
    });
    const toggleRaw = () => setRawOpen((open) => {
        try { localStorage.setItem(RAW_TOGGLE_STORAGE, open ? '0' : '1'); } catch { /* not stored */ }
        return !open;
    });
    // `saved` comes from the saved baseline (savedValue), so it stays right
    // after a save; the working copy only knows the value it was loaded with.
    const rawFor = (slot, used) => {
        if (!showRaw || !rawOpen) return undefined;
        const base = (savedValue || value)[slot];
        const saved = base?.saved;
        // Same compare as the save: a save only writes a slot whose chips changed.
        const now = serializeSlot(slot, used);
        return {
            setting: SLOT_SETTINGS[slot],
            saved:   saved === null || saved === undefined ? null : String(saved),
            pending: now !== serializeSlot(slot, base?.used) ? now : null,
        };
    };
    const onChangeSlot = (slotKey, used) => onChange && onChange(slotKey, { used });

    const labelFor = useCallback((k) => {
        const shopLabel = shopFieldLabels && shopFieldLabels[k];
        if (shopLabel) return shopLabel;
        const meta = FIELD_BY_KEY.get(k);
        return meta ? tr(meta.i18nKey, meta.fallback) : k;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopFieldLabels, t]);

    // Only field keys that aren't already in the slot's `used` list show up
    // in the picker — adding the same field twice doesn't change what the
    // storefront renders, so deduping here keeps the UI tidy.
    const availableFor = (used) => {
        const usedKeys = new Set(used.filter(isTokenChip).map(c => c.key));
        return ALL_FIELDS.filter(f => !usedKeys.has(f.key));
    };

    const slotUsed = (slotKey) => Array.isArray(value[slotKey]?.used) ? value[slotKey].used : [];
    // F13: memoise per slot — typing free text in ONE slot used to rebuild
    // all four Set+filter passes and re-render ~96 available-field chips.
    const usedBrowserTab    = slotUsed('browserTab');
    const usedPageHeading   = slotUsed('pageHeading');
    const usedPageSub       = slotUsed('pageSubheading');
    const usedUrlSlug       = slotUsed('urlSlug');
    const usedSchemaName    = slotUsed('schemaName');
    const availBrowserTab   = useMemo(() => availableFor(usedBrowserTab),  [usedBrowserTab]);
    const availPageHeading  = useMemo(() => availableFor(usedPageHeading), [usedPageHeading]);
    const availPageSub      = useMemo(() => availableFor(usedPageSub),     [usedPageSub]);
    const availUrlSlug      = useMemo(() => availableFor(usedUrlSlug),     [usedUrlSlug]);
    const availSchemaName   = useMemo(() => availableFor(usedSchemaName),  [usedSchemaName]);

    return (
        <ConfigCard
            anchor="cfg-card-pdi-title-url"
            title={tr('cfg_pdi_title_url_title', 'Product title & URL')}
            description={tr('cfg_pdi_title_url_desc', 'How the product is identified across the site. Add fields and free text — for example "brand (id)" yields "Braxx (2231)".')}
            theme={theme}
            headerActions={showRaw ? (
                <ButtonBase
                    onClick={toggleRaw}
                    title={tr('sft_raw_toggle_help', 'SoftTouch only: show the value exactly as it is stored on the shop')}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 1, py: 0.4, borderRadius: 999,
                        border: '1px solid', borderColor: rawOpen ? 'primary.main' : 'divider',
                        color: rawOpen ? 'primary.main' : 'text.secondary',
                        backgroundColor: rawOpen ? 'rgba(25,118,210,0.06)' : 'transparent',
                        fontSize: '0.7rem', fontWeight: 600,
                        ...focusRing,
                    }}
                >
                    <CodeIcon sx={{ fontSize: 14 }} />
                    {tr('sft_raw_toggle', 'Stored values')}
                </ButtonBase>
            ) : undefined}
        >
            <Stack spacing={2.5}>
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_browser_tab', 'Browser tab title')}
                    helpText={tr('cfg_pdi_title_browser_tab_help', 'Shown in the browser tab and search results. Free text allowed (e.g. "name (reference)").')}
                    slot="browserTab"
                    used={usedBrowserTab}
                    available={availBrowserTab}
                    allowText
                    onSet={(used) => onChangeSlot('browserTab', used)}
                    labelFor={labelFor}
                    tr={tr}
                    raw={rawFor('browserTab', usedBrowserTab)}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_page_heading', 'Page heading on the product page')}
                    helpText={tr('cfg_pdi_title_page_heading_help', 'The big title shown above the product. Free text allowed.')}
                    slot="pageHeading"
                    used={usedPageHeading}
                    available={availPageHeading}
                    allowText
                    onSet={(used) => onChangeSlot('pageHeading', used)}
                    labelFor={labelFor}
                    tr={tr}
                    raw={rawFor('pageHeading', usedPageHeading)}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_page_subheading', 'Page subheading')}
                    helpText={tr('cfg_pdi_title_page_subheading_help', 'Smaller line shown right below the page heading.')}
                    slot="pageSubheading"
                    used={usedPageSub}
                    available={availPageSub}
                    allowText
                    onSet={(used) => onChangeSlot('pageSubheading', used)}
                    labelFor={labelFor}
                    tr={tr}
                    raw={rawFor('pageSubheading', usedPageSub)}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_url_slug', 'Product URL slug')}
                    helpText={tr('cfg_pdi_title_url_slug_help', 'Becomes part of the web address (e.g. /shop/dames/broek/braxx-2231). Free text not supported here.')}
                    slot="urlSlug"
                    used={usedUrlSlug}
                    available={availUrlSlug}
                    allowText={false}
                    onSet={(used) => onChangeSlot('urlSlug', used)}
                    labelFor={labelFor}
                    tr={tr}
                    raw={rawFor('urlSlug', usedUrlSlug)}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_schema_name', 'Product name for Google')}
                    helpText={tr('cfg_pdi_title_schema_name_help', 'The name search engines get in the product data (schema). Leave empty to keep the reference, as before.')}
                    slot="schemaName"
                    used={usedSchemaName}
                    available={availSchemaName}
                    allowText
                    onSet={(used) => onChangeSlot('schemaName', used)}
                    labelFor={labelFor}
                    tr={tr}
                    raw={rawFor('schemaName', usedSchemaName)}
                />
            </Stack>
        </ConfigCard>
    );
};

ProductTitleAndUrlCard.propTypes = {
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    shopFieldLabels: PropTypes.object,
    savedValue:         PropTypes.object,
    showRaw:            PropTypes.bool,
    theme:              PropTypes.string,
    t:                  PropTypes.func,
};

// Parse a free-form template string ("name (reference)" → chip array) by
// scanning for known field-key tokens. The longest keys are matched first so
// `category_10` (not a thing today but defensive) wouldn't be mistaken for
// `category_1`. Anything between matches becomes a literal-text chip.
export const parseTemplateToChips = (str) => {
    const s = String(str ?? '');
    if (!s) return [];
    const keys = ALL_FIELDS.map(f => f.key).slice().sort((a, b) => b.length - a.length);
    const out  = [];
    let i = 0;
    while (i < s.length) {
        // Try to match a token at position i.
        let matched = null;
        for (const k of keys) {
            if (s.substr(i, k.length) === k) {
                // Avoid mid-word matches (e.g. "brand" inside "rebrand"); we
                // only treat it as a token when the neighbouring characters
                // aren't part of a longer identifier.
                const prev = i > 0 ? s[i - 1] : '';
                const next = s[i + k.length] || '';
                const wordChar = (c) => /[A-Za-z0-9_]/.test(c);
                if (!wordChar(prev) && !wordChar(next)) {
                    matched = k;
                    break;
                }
            }
        }
        if (matched) {
            out.push(tokenChip(matched));
            i += matched.length;
        } else {
            // Accumulate literal text until the next token (or end of string).
            let textEnd = i + 1;
            while (textEnd < s.length) {
                let found = false;
                for (const k of keys) {
                    if (s.substr(textEnd, k.length) === k) {
                        const prev = s[textEnd - 1];
                        const next = s[textEnd + k.length] || '';
                        const wordChar = (c) => /[A-Za-z0-9_]/.test(c);
                        if (!wordChar(prev) && !wordChar(next)) {
                            found = true;
                            break;
                        }
                    }
                }
                if (found) break;
                textEnd++;
            }
            const text = s.slice(i, textEnd);
            if (text) out.push(textChip(text));
            i = textEnd;
        }
    }
    return out;
};

// Serialise a chip array back into the legacy template string. Tokens emit
// their key, text chips emit their literal text — concatenation preserves
// any user-typed separators (spaces, parens, dashes). Two fields directly
// next to each other get a space between them: "description_1color" would
// glue the values together on the storefront AND no longer parse back into
// two fields (parseTemplateToChips only matches whole words).
export const serializeChipsToTemplate = (chips) =>
    (chips || []).map((c, i, all) => {
        if (isTextChip(c)) return c.text || '';
        const prev = all[i - 1];
        return (prev && isTokenChip(prev) ? ' ' : '') + c.key;
    }).join('');

// For URL slug: only token chips matter, join with `,`.
export const serializeChipsToCsv = (chips) =>
    (chips || []).filter(isTokenChip).map(c => c.key).join(',');

// Parse a CSV ("category_1,category_2,reference") back into token chips
// (URL slug doesn't allow literal text).
export const parseCsvToChips = (str) =>
    String(str || '')
        .split(',')
        .map(k => k.trim())
        .filter(Boolean)
        .map(k => tokenChip(k));

export default ProductTitleAndUrlCard;
